import { VariantProps, cva } from 'class-variance-authority';
import { DetailedHTMLProps, FC, ForwardedRef, OptionHTMLAttributes, SelectHTMLAttributes, forwardRef } from 'react';
import { ISelectOption } from '../helpers/forms.types';
import { twMerge } from 'tailwind-merge';
import styles from './Select.module.scss';

const selectVariants = cva(
    [
        styles['select'],
        'flex items-center',
        'w-full',
        'font-medium',
        'border border-grayscale-300 ',
        'placeholder:text-grayscale-400',
        'focus:border-primary focus:outline focus:outline-2 focus:outline-primary/10',
    ],
    {
        variants: {
            size: {
                sm: ['text-sm pl-4 pr-9 h-8 rounded-md bg-[right_1rem_center]'],
                normal: ['text-sm pl-4 pr-9 h-9 rounded-md bg-[right_1rem_center]'],
                lg: ['text-base pl-[18px] pr-9 h-10 rounded-md bg-[right_18px_center]'],
            },
            invalid: {
                true: ['border-danger-500 outline outline-danger-50 focus:border-danger-500 focus:outline-danger-50'],
                false: [],
            },
            valid: {
                true: [
                    'border-success-500 outline outline-success-50 focus:border-success-500 focus:outline-success-50',
                ],
                false: [],
            },
            disabled: {
                true: ['opacity-70'],
                false: [],
            },
        },
        defaultVariants: {
            size: 'normal',
            disabled: false,
            invalid: false,
            valid: false,
        },
    }
);

export interface SelectProps
    extends Omit<DetailedHTMLProps<SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>, 'children' | 'size'>,
        VariantProps<typeof selectVariants> {
    options?: ISelectOption[];
    ref?: ForwardedRef<HTMLSelectElement>;
    optionProps?: DetailedHTMLProps<OptionHTMLAttributes<HTMLOptionElement>, HTMLOptionElement>;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>((props, ref) => {
    const { options = [], size, invalid, valid, disabled, className, optionProps, ...rest } = props;

    return (
        <select
            {...rest}
            ref={ref}
            disabled={disabled}
            className={twMerge(selectVariants({ size, invalid, valid, disabled, className }))}
        >
            {options.map((option, i) => (
                <option {...optionProps} key={i} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    );
});

export default Select;
